import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, MenuItem, Icon, MenuDivider } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Select2 } from '@blueprintjs/select';
import { ModelInfo } from '../../stores/ChatStoreMST';
import './ModelSelector.css';

interface ModelSelectorProps {
  selectedModel: string;
  availableModels: ModelInfo[];
  onSelectModel: (model: string) => void;
}

// Helper to extract provider name from model provider string
const getProviderName = (provider: string): string => {
  // Handle OpenRouter providers (e.g., "OpenAI via OpenRouter" -> "OpenAI")
  if (provider.includes('via OpenRouter')) {
    return provider.split(' via')[0];
  }
  // Handle other providers
  return provider.split(' ')[0];
};

// Sort providers with a custom order
const getProviderOrder = (provider: string): number => {
  const order = {
    'OpenAI': 0,
    'Anthropic': 1,
    'Google': 2,
    'Meta': 3,
    'Mistral': 4,
    'xAI': 5,
    'Cohere': 6,
    'Perplexity': 7,
    'Alibaba': 8,
    'DeepSeek': 9
  };
  return order[provider] ?? 999;
};

const ModelSelector: React.FC<ModelSelectorProps> = observer(({
  selectedModel,
  availableModels = [],
  onSelectModel
}) => {
  // Group models by provider
  const groupedModels = useMemo(() => {
    const groups: Record<string, ModelInfo[]> = {};
    
    // Filter out disabled models and group by provider
    const enabledModels = availableModels.filter(m => m && m.enabled !== false);
    const disabledModels = availableModels.filter(m => m && m.enabled === false);
    
    // Group enabled models
    enabledModels.forEach(model => {
      const providerName = getProviderName(model.provider);
      if (!groups[providerName]) {
        groups[providerName] = [];
      }
      groups[providerName].push(model);
    });
    
    // Add disabled models as a separate group if any exist
    if (disabledModels.length > 0) {
      groups['_disabled'] = disabledModels;
    }
    
    // Sort providers and models within each provider
    const sortedGroups = Object.entries(groups)
      .sort((a, b) => {
        // Put disabled at the end
        if (a[0] === '_disabled') return 1;
        if (b[0] === '_disabled') return -1;
        
        // Sort by provider order
        return getProviderOrder(a[0]) - getProviderOrder(b[0]);
      })
      .map(([provider, models]) => ({
        provider,
        models: models.sort((a, b) => a.displayName.localeCompare(b.displayName))
      }));
    
    return sortedGroups;
  }, [availableModels]);
  
  // Flatten models for the Select2 component with group headers
  const flattenedItems = useMemo(() => {
    const items: (ModelInfo | { isHeader: true; title: string; isDivider?: boolean })[] = [];
    
    groupedModels.forEach((group, index) => {
      // Add divider between groups (except before first group)
      if (index > 0) {
        items.push({ isHeader: true, title: '', isDivider: true });
      }
      
      // Add group header
      const headerTitle = group.provider === '_disabled' 
        ? 'Not Configured' 
        : `${group.provider} Models`;
      items.push({ isHeader: true, title: headerTitle, isDivider: false });
      
      // Add models in this group
      items.push(...group.models);
    });
    
    return items;
  }, [groupedModels]);
  
  const currentModel = availableModels.find(m => m && m.model === selectedModel) || availableModels[0];

  const renderItem = (item: any, { handleClick, modifiers }: any) => {
    // Check if this is a divider
    if (item.isHeader && item.isDivider) {
      return <MenuDivider key={`divider-${Math.random()}`} />;
    }
    
    // Check if this is a header
    if (item.isHeader) {
      return (
        <MenuItem
          key={`header-${item.title}`}
          disabled={true}
          text={
            <div className="model-group-header">
              {item.title}
            </div>
          }
        />
      );
    }
    
    // Regular model item
    const model = item as ModelInfo;
    if (!model || !modifiers.matchesPredicate) {
      return null;
    }
    
    const isDisabled = model.enabled === false;
    
    return (
      <MenuItem
        active={modifiers.active}
        disabled={isDisabled || modifiers.disabled}
        key={model.model}
        onClick={isDisabled ? undefined : handleClick}
        text={
          <div className={`model-item ${isDisabled ? 'model-disabled' : ''}`}>
            <div className="model-info">
              <span className="model-name">
                {model.displayName}
              </span>
              <span className="model-id">{model.model}</span>
            </div>
            <div className="model-features">
              {!isDisabled && (
                <>
                  {model.supportedFeatures?.streaming && (
                    <span className="feature-badge" title="Streaming">
                      <Icon icon={IconNames.FEED} iconSize={10} />
                    </span>
                  )}
                  {model.supportedFeatures?.vision && (
                    <span className="feature-badge" title="Vision">
                      <Icon icon={IconNames.EYE_OPEN} iconSize={10} />
                    </span>
                  )}
                  {model.supportedFeatures?.documents && (
                    <span className="feature-badge" title="Documents">
                      <Icon icon={IconNames.DOCUMENT} iconSize={10} />
                    </span>
                  )}
                  {model.supportedFeatures?.functions && (
                    <span className="feature-badge" title="Functions">
                      <Icon icon={IconNames.FUNCTION} iconSize={10} />
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        }
        shouldDismissPopover={false}
      />
    );
  };

  const filterItem = (query: string, item: any) => {
    // Don't filter headers
    if (item.isHeader) return true;
    
    const model = item as ModelInfo;
    if (!model) return false;
    
    // If no query, show all models
    if (!query || query.trim() === '') return true;
    
    const lowerQuery = query.toLowerCase();
    const displayNameMatch = model.displayName ? model.displayName.toLowerCase().includes(lowerQuery) : false;
    const providerMatch = model.provider ? model.provider.toLowerCase().includes(lowerQuery) : false;
    const modelIdMatch = model.model ? model.model.toLowerCase().includes(lowerQuery) : false;
    
    return displayNameMatch || providerMatch || modelIdMatch;
  };

  const handleItemSelect = (item: any) => {
    // Ignore headers
    if (item.isHeader) return;
    
    const model = item as ModelInfo;
    if (model && model.enabled !== false) {
      onSelectModel(model.model);
    }
  };

  return (
    <div className="model-selector">
      <Select2
        items={flattenedItems}
        itemPredicate={filterItem}
        itemRenderer={renderItem}
        noResults={<MenuItem disabled text="No models available" />}
        onItemSelect={handleItemSelect}
        popoverProps={{
          minimal: true,
          placement: 'bottom-end',
          popoverClassName: 'model-selector-popover',
          modifiers: {
            arrow: { enabled: false },
            preventOverflow: { enabled: true },
            flip: { enabled: true }
          }
        }}
        filterable={true}
        resetOnSelect={false}
      >
        <Button
          rightIcon={IconNames.CARET_DOWN}
          text={
            <div className="selected-model">
              <span className="selected-model-name">{currentModel?.displayName || 'Select Model'}</span>
              {currentModel && (
                <span className="selected-model-provider">
                  {getProviderName(currentModel.provider)}
                </span>
              )}
            </div>
          }
          minimal
          className="model-selector-button"
        />
      </Select2>
    </div>
  );
});

export default ModelSelector;